/**
 * MKS-136 — the project's billing schedule and the phases sales drafts on a
 * lead.
 *
 * Billing periods are NOT ticket milestones: a ticket keeps its sprint and may
 * additionally be tied to one billing period ("must be delivered for payment
 * 2"), which is what `setTaskBillingPeriod` writes.
 */
import { apiGet, apiPost, apiPatch, apiPut } from './client';

export type BillingPeriodType = 'recurring' | 'phase';
export type BillingPeriodStatus =
  | 'planned'
  | 'ready_to_invoice'
  | 'invoiced'
  | 'cancelled';

export interface BillingPeriod {
  id: number;
  projectId: number;
  name: string;
  sequence: number;
  type: BillingPeriodType;
  /** YYYY-MM-DD */
  startDate: string;
  plannedEndDate: string;
  actualEndDate: string | null;
  plannedAmount: number | null;
  currency: string | null;
  status: BillingPeriodStatus;
  invoiceId: number | null;
  /**
   * The range an invoice for this period bills. For a phase period this opens
   * after the PREVIOUS period's actual end, so it can span several months.
   */
  billedFrom: string;
  billedTo: string;
  createdAt: string;
  updatedAt: string;
}

export interface BillingPeriodSumState {
  periodTotal: number;
  contractAmount: number | null;
  difference: number | null;
  matches: boolean;
}

export interface BillingSchedule {
  projectId: number;
  frequency: string | null;
  /** True when the project is Fixed Price, so the sum rule applies. */
  fixedPrice: boolean;
  contractCurrency: string | null;
  sum: BillingPeriodSumState;
  periods: BillingPeriod[];
  /** True for a monthly/quarterly project — the one that can regenerate. */
  recurring: boolean;
  /** Whether THIS user may change the schedule. Affordance; the API re-checks. */
  canEdit: boolean;
}

export interface RegenerationResult {
  created: number;
  updated: number;
  unchanged: number;
  skippedFrozen: number;
  schedule: BillingSchedule;
}

export interface CreateBillingPeriodInput {
  name: string;
  startDate?: string;
  plannedEndDate: string;
  plannedAmount?: number | null;
  currency?: string | null;
}

export interface UpdateBillingPeriodInput {
  name?: string;
  startDate?: string;
  plannedEndDate?: string;
  plannedAmount?: number | null;
  currency?: string | null;
}

export function fetchBillingSchedule(projectId: number) {
  return apiGet<{ data: BillingSchedule }>(`/projects/${projectId}/billing-periods`);
}

export function createBillingPeriod(projectId: number, input: CreateBillingPeriodInput) {
  return apiPost<{ data: BillingPeriod }>(`/projects/${projectId}/billing-periods`, input);
}

export function regenerateBillingPeriods(projectId: number) {
  return apiPost<{ data: RegenerationResult }>(
    `/projects/${projectId}/billing-periods/regenerate`,
    {},
  );
}

export function updateBillingPeriod(periodId: number, input: UpdateBillingPeriodInput) {
  return apiPatch<{ data: BillingPeriod }>(`/billing-periods/${periodId}`, input);
}

export function markBillingPeriodDone(periodId: number, actualEndDate?: string) {
  return apiPost<{ data: BillingPeriod }>(
    `/billing-periods/${periodId}/mark-done`,
    actualEndDate ? { actualEndDate } : {},
  );
}

export function cancelBillingPeriod(periodId: number) {
  return apiPost<{ data: BillingPeriod }>(`/billing-periods/${periodId}/cancel`, {});
}

export function setTaskBillingPeriod(taskId: number, billingPeriodId: number | null) {
  return apiPatch<{ data: { id: number; billingPeriodId: number | null } }>(
    `/tasks/${taskId}/billing-period`,
    { billingPeriodId },
  );
}

// ---------------------------------------------------------------------------
// Lead draft phases
// ---------------------------------------------------------------------------

export interface LeadBillingPhase {
  id: number;
  leadId: number;
  name: string;
  sequence: number;
  plannedEndDate: string | null;
  plannedAmount: number | null;
  currency: string | null;
}

export interface LeadBillingPhaseInput {
  name: string;
  plannedEndDate?: string | null;
  plannedAmount?: number | null;
  currency?: string | null;
}

export function fetchLeadBillingPhases(leadId: number) {
  return apiGet<{ data: LeadBillingPhase[] }>(`/leads/${leadId}/billing-phases`);
}

/** The draft is replaced wholesale — see the backend service for why. */
export function putLeadBillingPhases(leadId: number, phases: LeadBillingPhaseInput[]) {
  return apiPut<{ data: LeadBillingPhase[] }>(`/leads/${leadId}/billing-phases`, { phases });
}

// ---------------------------------------------------------------------------
// Display helpers
// ---------------------------------------------------------------------------

export const BILLING_PERIOD_STATUS_LABELS: Record<BillingPeriodStatus, string> = {
  planned: 'Planned',
  ready_to_invoice: 'Ready to invoice',
  invoiced: 'Invoiced',
  cancelled: 'Cancelled',
};

export const BILLING_PERIOD_STATUS_CLASSES: Record<BillingPeriodStatus, string> = {
  planned: 'bg-gray-100 text-gray-700',
  ready_to_invoice: 'bg-amber-100 text-amber-800',
  invoiced: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-400 line-through',
};
